import { Country, ErrorCode } from '@afterpaytouch/portal-api/types'
import { useRouter } from 'next/router'
import { RegistrationStatus, CheckoutStatus } from '../model'
import { PageNames } from '../model/monitoring'
import {
  useAuthentication,
  useConsumerSelector,
  useHasSavedPaymentMethods,
  useRegistration,
  useHasSavedAddresses,
  useConsumerLendingEligible,
  useCheckoutState,
  useIsConsumerLendingOrder,
  useIsRecoverableFlow,
  useCheckoutError,
  useIsPayMonthlyOnly,
  usePaymentTypeAllowInConvergence,
  useInlineArcadeComponentsEnabled,
  useIsShippingAddressValid,
  useAppRouter,
} from '../state'
import { PaymentType } from '@afterpay/types'

export enum Route {
  ADD_MOBILE = '/add-mobile',
  ADD_PAYMENT_METHOD = '/add-payment-method',
  // Apple Pay Later flow-specific routes (no base equivalents)
  ADJUST_AMOUNT = '/apple-paylater/adjust-amount',
  APPLE_PAYLATER_ADD_CARD = '/apple-paylater/add-card',
  APPLE_PAYLATER_INITIALISE = '/apple-paylater/initialise',
  APPLE_PAYLATER_INITIAL_LOADING = '/apple-paylater/initial-loading',
  CARD_EDUCATION = '/apple-paylater/card-education',
  CONSUMER_LENDING_SUMMARY = '/consumer-lending-summary',
  APPROVED_CREDIT = '/approved-credit',
  BANKING_SERVICE_UNAVAILABLE = '/banking-service-unavailable',
  CASH_AUTH = '/cash-auth',
  CREATE_PASSWORD = '/create-password',
  ENTER_EMAIL = '/login',
  ENTER_PASSWORD = '/password',
  EMPLOYMENT_DETAILS = '/employment-details',
  NEW_PAYMENT_METHOD = '/new-payment-method',
  NEW_SHIPPING_ADDRESS = '/new-shipping-address',
  EDIT_SHIPPING_ADDRESS = '/edit-shipping-address',
  EDIT_SHIPPING_ADDRESS_WITH_ID = '/edit-shipping-address/[id]',
  INITIATE_BANK_VERIFICATION = '/initiate-bank-verification',
  ONE_TIME_PASSWORD = '/login-one-time-code',
  PAY = '/pay',
  PAY_MONTHLY = '/pay-monthly',
  PAY_MONTHLY_ONLY = '/pay-monthly-only',
  PAYMENT_METHOD = '/payment-method',
  PAYMENT_OPTIONS = '/payment-options',
  PAYMENT_TYPES = '/payment-types',
  PREFERRED_PAYMENT_DAY = '/preferred-payment-day',
  LOGGED_IN = '/logged-in',
  SHIPPING_ADDRESS = '/shipping-address',
  SIGN_UP = '/sign-up',
  SUMMARY = '/summary',
  TERMINAL_ERROR = '/oh-no',
  AFF_TRUTH_IN_LENDING = '/aff-truth-in-lending',
  VERIFY = '/verify',
  VERIFY_CHALLENGE = '/verify-challenge',
  VERIFY_IDENTITY = '/verify-identity',
  VERIFY_LICENCE = '/verify-identity/licence',
  VERIFY_MEDICARE = '/verify-identity/medicare',
  VERIFY_PASSPORT = '/verify-identity/passport',
  VERIFY_ADDRESS = '/verify-identity/address',
  VERIFY_BIOMETRIC_LICENCE = '/verify-identity/biometric/licence',
  VERIFY_BIOMETRIC_PASSPORT = '/verify-identity/biometric/passport',
  VERIFYING_BIOMETRIC = '/verify-identity/biometric/verifying',
  VERIFY_BIOMETRIC_PERSONA = '/verify-identity/biometric/persona',
  VERIFY_IDENTITY_START = '/verify-identity-start',
  RETRY_VERIFY_LICENCE = '/verify-identity/retry/licence',
  RETRY_VERIFY_PASSPORT = '/verify-identity/retry/passport',
  RETRY_VERIFY_MEDICARE = '/verify-identity/retry/medicare', // only being used in unit test
  VERIFY_RETRY = '/verify-retry',
  CARD_SCAN = '/card-scan',
  PASSWORD_RESET_REQUIRED = '/password-reset-required',
  AMOUNT_TOO_HIGH = '/oh-no/amount_too_high',
  ORDER_LIMIT_EXCEEDED = '/oh-no/max_active_orders_reached',
  OVERDUE_PAYMENTS_CHECKOUT_PAYMENT_ELIGIBLE = '/oh-no/overdue_payments_checkout_payment_eligible',
  AFFORDABILITY_CREDIT_LIMIT = '/affordability/credit-limit',
  AFFORDABILITY_INCOME = '/affordability/income',
  AFFORDABILITY_EXPENSES = '/affordability/expenses',
  AFFORDABILITY_VERIFYING = '/affordability/verifying',
  AFFORDABILITY_JOINT_ACCOUNTS = '/affordability/joint-accounts',
  RETRY_CREDIT_FREEZE = '/retry-credit-freeze',
  INDEX = '/',
  INITIAL_LOADING = '/initial-loading',
  KYC_CONFIRM_DETAILS = '/kyc-profile-confirm-details',
  KYC_UPDATE_ADDRESS = '/kyc-profile-confirm-details/update-address',
  UNSUITABILITY_QUESTION_FORM = '/unsuitability-questions/form',
  CONTRACT_AGREEMENT = '/contract-agreement',
}

export const VERIFY_PREFETCH_ROUTE_COUNTRY_MAP = {
  [Country.US]: [],
  [Country.AU]: [Route.VERIFY_LICENCE, Route.VERIFY_MEDICARE, Route.VERIFY_PASSPORT],
  [Country.NZ]: [Route.VERIFY_LICENCE, Route.VERIFY_MEDICARE, Route.VERIFY_PASSPORT],
  [Country.GB]: [Route.VERIFY_BIOMETRIC_LICENCE, Route.VERIFY_BIOMETRIC_PASSPORT],
  [Country.CA]: [Route.VERIFY_BIOMETRIC_PERSONA],
}

export const getPrefetchRoutes = (country: Country): Route[] => {
  const VerifyRoutes = VERIFY_PREFETCH_ROUTE_COUNTRY_MAP[country]
  if (typeof VerifyRoutes === 'undefined') {
    return []
  }
  return VerifyRoutes
}

export const editShippingAddressRoute = (shippingAddressId: number | string | undefined): Route =>
  `${Route.EDIT_SHIPPING_ADDRESS}/${shippingAddressId}` as unknown as Route

export const routePageNames: Record<Route, PageNames> = {
  [Route.APPLE_PAYLATER_ADD_CARD]: PageNames.APPLE_PAYLATER_ADD_CARD,
  [Route.ADD_MOBILE]: PageNames.ADD_MOBILE,
  [Route.ADD_PAYMENT_METHOD]: PageNames.ADD_PAYMENT_METHOD,
  [Route.ADJUST_AMOUNT]: PageNames.ADJUST_AMOUNT,
  [Route.APPLE_PAYLATER_INITIALISE]: PageNames.APPLE_PAYLATER_INITIALISE,
  [Route.APPLE_PAYLATER_INITIAL_LOADING]: PageNames.APPLE_PAYLATER_INITIAL_LOADING,
  [Route.CARD_EDUCATION]: PageNames.CARD_EDUCATION,
  [Route.CONSUMER_LENDING_SUMMARY]: PageNames.CONSUMER_LENDING_SUMMARY,
  [Route.APPROVED_CREDIT]: PageNames.APPROVED_CREDIT,
  [Route.BANKING_SERVICE_UNAVAILABLE]: PageNames.BANKING_SERVICE_UNAVAILABLE,
  [Route.CASH_AUTH]: PageNames.CASH_AUTH,
  [Route.CREATE_PASSWORD]: PageNames.CREATE_PASSWORD,
  [Route.ENTER_EMAIL]: PageNames.LOGIN_EMAIL,
  [Route.ENTER_PASSWORD]: PageNames.LOGIN_PASSWORD,
  [Route.PASSWORD_RESET_REQUIRED]: PageNames.PASSWORD_RESET_REQUIRED,
  [Route.NEW_PAYMENT_METHOD]: PageNames.ADD_NEW_PAYMENT_METHOD,
  [Route.NEW_SHIPPING_ADDRESS]: PageNames.ADD_NEW_SHIPPING_ADDRESS,
  [Route.EDIT_SHIPPING_ADDRESS]: PageNames.EDIT_SHIPPING_ADDRESS,
  [Route.EDIT_SHIPPING_ADDRESS_WITH_ID]: PageNames.EDIT_SHIPPING_ADDRESS,
  [Route.ONE_TIME_PASSWORD]: PageNames.ONE_TIME_PASSWORD,
  [Route.PAY]: PageNames.PAY,
  [Route.PAYMENT_METHOD]: PageNames.PAYMENT_METHOD,
  [Route.PAYMENT_OPTIONS]: PageNames.PAYMENT_OPTIONS,
  [Route.PAYMENT_TYPES]: PageNames.PAYMENT_TYPES,
  [Route.PREFERRED_PAYMENT_DAY]: PageNames.PREFERRED_PAYMENT_DAY,
  [Route.INITIATE_BANK_VERIFICATION]: PageNames.INITIATE_BANK_VERIFICATION,
  [Route.LOGGED_IN]: PageNames.LOGGED_IN,
  [Route.SHIPPING_ADDRESS]: PageNames.SHIPPING_ADDRESS,
  [Route.SIGN_UP]: PageNames.SIGN_UP,
  [Route.SUMMARY]: PageNames.SUMMARY,
  [Route.TERMINAL_ERROR]: PageNames.TERMINAL_ERROR,
  [Route.AFF_TRUTH_IN_LENDING]: PageNames.AFF_TRUTH_IN_LENDING,
  [Route.VERIFY]: PageNames.VERIFY,
  [Route.VERIFY_CHALLENGE]: PageNames.VERIFY_CHALLENGE,
  [Route.VERIFY_IDENTITY]: PageNames.VERIFY_IDENTITY,
  [Route.VERIFYING_BIOMETRIC]: PageNames.VERIFYING_BIOMETRIC,
  [Route.VERIFY_LICENCE]: PageNames.VERIFY_LICENCE,
  [Route.VERIFY_MEDICARE]: PageNames.VERIFY_MEDICARE,
  [Route.VERIFY_PASSPORT]: PageNames.VERIFY_PASSPORT,
  [Route.VERIFY_BIOMETRIC_LICENCE]: PageNames.VERIFY_LICENCE,
  [Route.VERIFY_BIOMETRIC_PASSPORT]: PageNames.VERIFY_PASSPORT,
  [Route.VERIFY_IDENTITY_START]: PageNames.VERIFY_IDENTITY_START,
  [Route.RETRY_VERIFY_LICENCE]: PageNames.RETRY_VERIFY_HARD_ID,
  [Route.RETRY_VERIFY_PASSPORT]: PageNames.RETRY_VERIFY_HARD_ID,
  [Route.RETRY_VERIFY_MEDICARE]: PageNames.RETRY_VERIFY_HARD_ID,
  [Route.VERIFY_ADDRESS]: PageNames.SIGNUP_RETRY_ADDRESS,
  [Route.VERIFY_RETRY]: PageNames.SIGNUP_RETRY,
  [Route.CARD_SCAN]: PageNames.CARD_SCAN,
  [Route.AMOUNT_TOO_HIGH]: PageNames.LIMIT_EXCEEDED,
  [Route.ORDER_LIMIT_EXCEEDED]: PageNames.LIMIT_EXCEEDED,
  [Route.AFFORDABILITY_CREDIT_LIMIT]: PageNames.AFFORDABILITY_CREDIT_LIMIT,
  [Route.AFFORDABILITY_INCOME]: PageNames.AFFORDABILITY_INCOME,
  [Route.AFFORDABILITY_EXPENSES]: PageNames.AFFORDABILITY_EXPENSES,
  [Route.AFFORDABILITY_VERIFYING]: PageNames.AFFORDABILITY_VERIFYING,
  [Route.AFFORDABILITY_JOINT_ACCOUNTS]: PageNames.AFFORDABILITY_JOINT_ACCOUNTS,
  [Route.EMPLOYMENT_DETAILS]: PageNames.EMPLOYMENT_DETAILS,
  [Route.PAY_MONTHLY]: PageNames.PAY_MONTHLY,
  [Route.PAY_MONTHLY_ONLY]: PageNames.PAY_MONTHLY_ONLY,
  [Route.INDEX]: PageNames.INDEX,
  [Route.INITIAL_LOADING]: PageNames.INITIAL_LOADING,
  [Route.RETRY_CREDIT_FREEZE]: PageNames.RETRY_CREDIT_FREEZE,
  [Route.OVERDUE_PAYMENTS_CHECKOUT_PAYMENT_ELIGIBLE]: PageNames.OVERDUE_PAYMENTS_CHECKOUT_PAYMENT_ELIGIBLE,
  [Route.KYC_CONFIRM_DETAILS]: PageNames.KYC_CONFIRM_DETAILS,
  [Route.KYC_UPDATE_ADDRESS]: PageNames.KYC_UPDATE_ADDRESS,
  [Route.UNSUITABILITY_QUESTION_FORM]: PageNames.UNSUITABILITY_QUESTIONS,
  [Route.CONTRACT_AGREEMENT]: PageNames.CONTRACT_AGREEMENT,
  [Route.VERIFY_BIOMETRIC_PERSONA]: PageNames.VERIFY_BIOMETRIC_PERSONA,
}

export const useRoutePageName = (): PageNames => {
  const { asPath, pathname, query } = useRouter()
  const auth = useAuthentication()
  const checkoutError = useCheckoutError()
  const registration = useRegistration()
  const { verifyAccountMobileRequired = false, hasPassword = false } = useConsumerSelector()
  const isRegistrationFlow = registration.status >= RegistrationStatus.Created

  if (pathname === Route.VERIFY) {
    // This should match the logic on the verify page
    if (isRegistrationFlow) {
      return PageNames.REGISTER_VERIFY
    } else if (verifyAccountMobileRequired) {
      return PageNames.ADD_MOBILE_VERIFY
    } else if (auth.verificationRequired) {
      return PageNames.NEW_DEVICE_VERIFY
    } else if (checkoutError?.errorCode === ErrorCode.CheckoutConfirm2FARequired || checkoutError?.errorCode === ErrorCode.CheckoutConfirm2FARequiredNoEmail) {
      return PageNames.CHECKOUT_CONFIRM_VERIFY
    } else if (checkoutError?.errorCode === ErrorCode.ExtendedFraudAlert) {
      return PageNames.EFA_VERIFY
    } else if (!hasPassword) {
      return PageNames.FIRST_RETURN_VERIFY
    } else {
      return PageNames.RESET_VERIFY
    }
  } else if (typeof query?.document !== 'undefined' || typeof query?.error !== 'undefined') {
    return routePageNames[asPath]
  }
  return routePageNames[pathname] ?? PageNames.NOT_FOUND
}

export const useSummaryRoute = (): Route => {
  const isConsumerLendingOrder = useIsConsumerLendingOrder()
  const isRecoverableFlow = useIsRecoverableFlow()
  const recoverableRoute = useRecoverableTerminalErrorRoute()

  if (isRecoverableFlow) {
    return recoverableRoute
  }

  return isConsumerLendingOrder ? Route.CONSUMER_LENDING_SUMMARY : Route.SUMMARY
}

// For risk decline experiments
export const errorCodeRoute: Partial<Record<ErrorCode, Route>> = {
  [ErrorCode.AmountTooHigh]: Route.AMOUNT_TOO_HIGH,
  [ErrorCode.MaxActiveOrdersReached]: Route.ORDER_LIMIT_EXCEEDED,
  [ErrorCode.OverduePaymentsCheckoutPaymentEligible]: Route.OVERDUE_PAYMENTS_CHECKOUT_PAYMENT_ELIGIBLE,
}

export const useRecoverableTerminalErrorRoute = (): Route => {
  const checkoutState = useCheckoutState()
  const errorCode = checkoutState.error?.errorCode ?? ''

  return errorCodeRoute[errorCode]
}

export const useRouteBackLink = (): Route => {
  const { asPath, pathname } = useRouter()
  const hasSavedPaymentMethods = useHasSavedPaymentMethods()
  const hasSavedAddresses = useHasSavedAddresses()
  const summaryRoute = useSummaryRoute()
  const recoverableRoute = useRecoverableTerminalErrorRoute()
  const isRecoverableFlow = useIsRecoverableFlow()
  const isInlineArcadeComponentEnabled = useInlineArcadeComponentsEnabled(false)

  if (pathname === Route.EDIT_SHIPPING_ADDRESS_WITH_ID) {
    return isInlineArcadeComponentEnabled ? summaryRoute : Route.SHIPPING_ADDRESS
  }

  switch (asPath) {
    case Route.NEW_PAYMENT_METHOD:
      if (isInlineArcadeComponentEnabled) {
        return summaryRoute
      }
      if (hasSavedPaymentMethods) {
        return Route.PAYMENT_METHOD
      }
      if (isRecoverableFlow) {
        return recoverableRoute
      }
      return summaryRoute
    case Route.CARD_SCAN:
    case Route.PAYMENT_METHOD:
      return isRecoverableFlow ? recoverableRoute : summaryRoute
    case Route.SHIPPING_ADDRESS:
    case Route.PREFERRED_PAYMENT_DAY:
      return summaryRoute
    case Route.NEW_SHIPPING_ADDRESS:
      return hasSavedAddresses && !isInlineArcadeComponentEnabled ? Route.SHIPPING_ADDRESS : summaryRoute
    case Route.VERIFY_LICENCE:
    case Route.VERIFY_MEDICARE:
    case Route.VERIFY_PASSPORT:
      return Route.VERIFY_IDENTITY
    default:
      return asPath as Route
  }
}

export const isRoute = (a: any): a is Route => Object.values(Route).includes(a)

export const usePostLoginRoute = (): Route => {
  const isConsumerLendingEligible = useConsumerLendingEligible()
  const { status } = useCheckoutState()
  const isRecoverableFlow = useIsRecoverableFlow()
  const recoverableRoute = useRecoverableTerminalErrorRoute()
  const isPayMonthlyOnly = useIsPayMonthlyOnly()
  const isConsumerLendingAllowInConvergence = usePaymentTypeAllowInConvergence(PaymentType.PCL)

  if (isRecoverableFlow) {
    return recoverableRoute
  }

  if (status < CheckoutStatus.Starting) {
    return Route.LOGGED_IN
  }

  if (!isConsumerLendingAllowInConvergence) {
    return Route.SUMMARY
  }

  if (isConsumerLendingEligible && isPayMonthlyOnly) {
    // route to pay monthly only flow in US region
    return Route.PAY_MONTHLY_ONLY
  }

  return isConsumerLendingEligible ? Route.PAYMENT_TYPES : Route.SUMMARY
}

export const useNavigateToShippingAddress = (): (() => void) => {
  const router = useAppRouter()
  const hasSavedAddresses = useHasSavedAddresses()
  const hasValidShippingAddress = useIsShippingAddressValid()

  const routeToNavigateTo = !hasValidShippingAddress && !hasSavedAddresses ? Route.NEW_SHIPPING_ADDRESS : Route.SHIPPING_ADDRESS
  return () => router.push(routeToNavigateTo)
}

export const CONSUMER_LENDING_ROUTES = [
  Route.PAYMENT_OPTIONS,
  Route.PAYMENT_TYPES,
  Route.BANKING_SERVICE_UNAVAILABLE,
  Route.APPROVED_CREDIT,
  Route.EMPLOYMENT_DETAILS,
  Route.INITIATE_BANK_VERIFICATION,
  Route.AFF_TRUTH_IN_LENDING,
  Route.AFFORDABILITY_CREDIT_LIMIT,
  Route.AFFORDABILITY_INCOME,
  Route.AFFORDABILITY_EXPENSES,
  Route.AFFORDABILITY_VERIFYING,
  Route.AFFORDABILITY_JOINT_ACCOUNTS,
]
